import axios from 'axios';

import { getFiltersUrlParams } from '../utils/ladder.util';
import { toImageUrl } from '../utils/format.util';

import { SELLING_PLATFORM_OPTIONS, LISTING_TYPE_OPTIONS } from '../constants';

const SEARCH_URL = 'https://search.cardladder.com';
// const SEARCH_URL = 'local-search';

export default class SearchService {
  constructor(authorizationOrOptions) {
    if (authorizationOrOptions && typeof authorizationOrOptions === 'object') {
      this.authorization = authorizationOrOptions.authorization;
      this.baseUrl = authorizationOrOptions.baseUrl || SEARCH_URL;
      this.request = authorizationOrOptions.request;
    } else {
      this.authorization = authorizationOrOptions;
      this.baseUrl = SEARCH_URL;
      this.request = null;
    }
  }

  async httpRequest({ url, method = 'GET', data }) {
    if (this.request) {
      return this.request({ url, method, data });
    }

    const headers = {};

    if (this.authorization) {
      headers.authorization = this.authorization;
    }

    const response = await axios({
      url,
      method,
      data,
      headers,
    });

    return response.data;
  }

  async search({
    index,
    query,
    filters,
    page,
    limit,
    sort,
    direction,
    fuzziness,
    includeHidden,
  }) {
    try {
      let url = `${this.baseUrl}/search?index=${index}&query=${encodeURIComponent(query || '')}`;

      if (page) {
        url += `&page=${page - 1}`;
      }

      if (limit) {
        url += `&limit=${limit}`;
      }

      if (filters && filters.verified === 'verified' && (index === 'salesarchive' || index === 'collectioncards')) {
        url += '&exists=verified';
      }

      if (filters && filters.verified === 'unverified' && (index === 'salesarchive' || index === 'collectioncards')) {
        url += '&notexists=unverified';
      }

      if (filters) {
        url += `&filters=${encodeURIComponent(getFiltersUrlParams(filters))}`;
      }

      if (sort) {
        url += `&sort=${sort}`;
      }

      if (direction) {
        url += `&direction=${direction}`;
      }

      if (fuzziness) {
        url += '&fuzziness=true';
      }

      if (includeHidden) {
        url += '&includeHidden=true';
      }

      const data = await this.httpRequest({ url });

      const hits = data.hits.map((hit) => {
        const item = {
          ...hit,
          itemId: hit.id,
          listingId: hit.id,
          price: hit.price ? parseFloat(hit.price) : null,
          bids: hit.bids ? parseInt(hit.bids, 10) : null,
          watchers: hit.watchers ? parseInt(hit.watchers, 10) : null,
          date: hit.date ? new Date(hit.date) : '',
          endDate: hit.endDate ? new Date(hit.endDate) : '',
          endTime: hit.endTime ? new Date(hit.endTime) : '',
          dateAdded: hit.dateAdded ? new Date(hit.dateAdded) : '',
          datePurchased: hit.datePurchased ? new Date(hit.datePurchased) : '',
          createdDate: hit.createdDate ? new Date(hit.createdDate) : '',
          updatedDate: hit.updatedDate ? new Date(hit.updatedDate) : '',
          dateSubmitted: hit.dateSubmitted ? new Date(hit.dateSubmitted) : '',
          platform: hit.platform && SELLING_PLATFORM_OPTIONS.find(({ value }) => value === hit.platform) ? SELLING_PLATFORM_OPTIONS.find(({ value }) => value === hit.platform).label : hit.platform || '',
          listingType: hit.listingType && LISTING_TYPE_OPTIONS.find(({ value }) => value === hit.listingType) ? LISTING_TYPE_OPTIONS.find(({ value }) => value === hit.listingType).label : hit.listingType || '',
        };

        if (index === 'pendingsales') {
          item.platform = hit.platform;
          item.listingType = hit.listingType;
        }

        if (index === 'cards' || index === 'players') {
          item.image = toImageUrl(`cards/thumb_${index === 'players' ? hit.keyCardId : hit.id}`);
        } else if (index === 'watchlistcards') {
          item.image = toImageUrl(`cards/thumb_${hit.cardId}`);
        }

        return item;
      });

      return {
        ...data,
        hits,
      };
    } catch (e) {
      throw e;
    }
  }

  async getDoc(index, id) {
    try {
      const hit = await this.httpRequest({ url: `${this.baseUrl}/doc?index=${index}&id=${id}` });

      const doc = {
        ...hit,
      };

      if (hit.dateAdded) {
        doc.dateAdded = new Date(hit.dateAdded);
      }

      if (hit.updatedDate) {
        doc.updatedDate = new Date(hit.updatedDate);
      }

      if (hit.date) {
        doc.date = new Date(hit.date);
      }

      return doc;
    } catch (e) {
      throw e;
    }
  }

  async searchSuggestions(input) {
    try {
      if (!input || !String(input).trim()) {
        return [];
      }

      const suggestions = await this.httpRequest({ url: `${this.baseUrl}/suggestions?input=${input}` });

      return suggestions;
    } catch (e) {
      throw e;
    }
  }

  async searchImage({
    source,
    queryRecords,
    limit = 50,
    sort = 'date',
    direction = 'desc',
  }) {
    try {
      const body = {
        source,
        queryRecords,
        limit,
        sort,
        direction,
      };

      const data = await this.httpRequest({
        url: `${this.baseUrl}/image`,
        method: 'POST',
        data: body,
      });

      const hits = data.hits.map((hit) => {
        const item = {
          ...hit,
          date: hit.date ? new Date(hit.date) : '',
          platform: hit.platform && SELLING_PLATFORM_OPTIONS.find(({ value }) => value === hit.platform) ? SELLING_PLATFORM_OPTIONS.find(({ value }) => value === hit.platform).label : hit.platform || '',
          listingType: hit.listingType && LISTING_TYPE_OPTIONS.find(({ value }) => value === hit.listingType) ? LISTING_TYPE_OPTIONS.find(({ value }) => value === hit.listingType).label : hit.listingType || '',
        };

        if (source === 'cards') {
          item.image = toImageUrl(`cards/thumb_${hit.id}`);
        } else if (source === 'salesarchive') {
          item.image = hit._url;
        }

        return item;
      });

      if (source === 'cards' && hits.length) {
        const response = await this.search({
          index: 'cards',
          limit: hits.length,
          filters: { ids: hits.map(({ cardId }) => cardId) },
        });

        if (response && response.hits && response.hits.length) {
          hits.forEach((hit, i) => {
            const ladderCard = response.hits.find(({ id }) => id === hit.cardId);

            if (ladderCard && ladderCard.currentValue) {
              hits[i].currentValue = ladderCard.currentValue;
            }
          });
        }
      } else if (source === 'salesarchive' && hits.length) {
        const response = await this.search({
          index: 'salesarchive',
          limit: hits.length,
          filters: { ids: hits.map(({ id }) => id) },
        });

        if (response && response.hits && response.hits.length) {
          hits.forEach((hit, i) => {
            const sale = response.hits.find(({ id }) => id === hit.id);

            if (sale && sale.cardId) {
              hits[i].cardId = sale.cardId;
              hits[i].url = sale.url;
            }
          });
        }
      }

      return {
        ...data,
        hits,
      };
    } catch (e) {
      throw e;
    }
  }
}
